// src/utils/dateUtils.js
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
// Si usas más idiomas, impórtalos aquí
// Si usas zona horaria con dayjs: 
// import utc from 'dayjs/plugin/utc';
// import timezone from 'dayjs/plugin/timezone';
// dayjs.extend(utc);
// dayjs.extend(timezone);

/**
 * ========== FECHAS GLOBAL: DAYJS (multiidioma, navegador, etc) ==========
 */

/**
 * Formatea una fecha con dayjs, idioma configurable, formato configurable.
 * @param {string|Date|dayjs} dt - Fecha a formatear
 * @param {Object} opts
 * @param {string} opts.locale - 'en', 'es', etc (default navegador)
 * @param {string} opts.format - Ej: 'L LT', 'YYYY-MM-DD HH:mm', etc (default 'L LT')
 * @returns {string}
 */
export function formatDate(dt, opts = {}) {
  if (!dt) return '';
  const locale = opts.locale || navigator.language || 'en';
  const format = opts.format || 'L LT';
  return dayjs(dt).locale(locale).format(format);
}

/**
 * Formatea fecha corta (solo día)
 */
export function formatShortDate(dt, locale) {
  return formatDate(dt, { locale, format: 'L' });
}

/**
 * Formatea solo la hora (HH:mm o HH:mm:ss)
 */
export function formatTime(dt, locale, withSeconds = false) {
  return formatDate(dt, { locale, format: withSeconds ? 'HH:mm:ss' : 'LT' });
}

/**
 * Devuelve string ISO 8601 UTC
 */
export function toISOString(dt) {
  if (!dt) return '';
  return dayjs(dt).toISOString();
}


/**
 * ========== FECHAS DUBAI FIJO (siempre en 'en-AE', zona Asia/Dubai, 24h) ==========
 */

/**
 * Fecha + hora, con opción segundos, SIEMPRE en inglés y Dubai
 */
export function formatDateDubai(dt, opts = {}) {
  if (!dt) return '';
  const showSeconds = !!opts.seconds;
  return new Date(dt).toLocaleString('en-AE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
    hour12: false,
    timeZone: 'Asia/Dubai'
  });
}

/**
 * Solo fecha, en inglés y Dubai
 */
export function formatShortDubai(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleDateString('en-AE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Dubai'
  });
}

/**
 * Solo hora, en inglés y Dubai
 */
export function formatTimeDubai(dt, opts = {}) {
  if (!dt) return '';
  const showSeconds = !!opts.seconds;
  return new Date(dt).toLocaleTimeString('en-AE', {
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
    hour12: false,
    timeZone: 'Asia/Dubai'
  });
}


/**
 * ========== FECHAS: LOCALE DEL NAVEGADOR + ZONA DUBAI ==========
 */
export function formatDubaiLocale(dt, opts = {}) {
  if (!dt) return '';
  const locale = opts.locale || navigator.language || 'en-AE';
  const showSeconds = !!opts.seconds;
  return new Date(dt).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
    hour12: false,
    timeZone: 'Asia/Dubai'
  });
}

/**
 * ========== FECHAS: LOCALE Y ZONA DEL NAVEGADOR (user local) ==========
 */
export function formatBrowser(dt, opts = {}) {
  if (!dt) return '';
  const locale = opts.locale || navigator.language || 'en';
  const showSeconds = !!opts.seconds;
  return new Date(dt).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
    hour12: false
    // sin timeZone -> usa la del navegador
  });
}

// ==================== HELPERS OPCIONALES =====================

/**
 * Convierte un string ISO (UTC) recibido del backend a Date Dubai local
 */
export function parseUtcToDubai(dateStr) {
  if (!dateStr) return null;
  // Esto requiere dayjs con timezone plugin activado
  // return dayjs.utc(dateStr).tz('Asia/Dubai');
  // Si solo usas Date, basta con: new Date(dateStr)
  return new Date(dateStr); // ya puedes pasarlo a formatDubai()
}

/**
 * Convierte fecha seleccionada en picker (Dubai local) a UTC string para backend
 */
export function dubaiToUtcIso(dt) {
  // Si usas dayjs con timezone:
  // return dayjs(dt).tz('Asia/Dubai').utc().toISOString();
  // Si usas Date puro:
  return new Date(dt).toISOString();
}

